import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { Table } from '@interstate/components/Table';
import type { OnChangeSorterType, SortableDetailsType, TableColumn } from '@interstate/components/Table';
import { ResizeObserver } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import type { ProgramQuote, ProgramsSortSelection } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE } from '@makemydeal/dr-platform-types';

import { removeColumns, reorderColumnsByDataIndex, withFrozenColumns } from '../../utils/mapUtils';
import { PI_GRID_COMPACT_FROZEN_COLUMN_COUNT, PI_GRID_FROZEN_COLUMN_BREAKPOINT, PI_GRID_FROZEN_COLUMN_COUNT } from './constants';
import { withLiveAmountColumn, withLivePaymentColumn } from './PaymentCell';
import {
    balloonColumnOrder,
    createColumnsWithSorter,
    enhancedBalloonColumnOrder,
    enhancedFinanceColumnsOrder,
    enhancedLeaseColumnsOrder,
    financeColumnsOrder,
    getCashExemptColumns,
    getFinanceOnlyColumns,
    getGrossColumns,
    leaseColumnsOrder,
    leaseOnlyColumns,
    placeRebatesAfterRates,
    toCompactColumnsOrder,
    programColumnSorters,
    withRestoredSortOrder
} from './programColumns';
import { TableWrapperForDealCentral } from './styles';
import { findFrozenRegionCells, getRowClassName as getRowClassNameUtil, measureFrozenRegionOffset } from './utils';

const SORTABLE_DETAILS: SortableDetailsType = { defaultSortOrder: 'ascend', sorter: programColumnSorters };

export type ProgramsTableRow = ProgramQuote & { key: string };

export type ProgramsTableLiveAmounts = {
    appliedKey: string;
    payment?: number;
    baselinePayment?: number;
    rebates?: number;
    baselineRebates?: number;
};

export type ProgramsTableProps = {
    data: ProgramsTableRow[];
    offerType: OfferType;
    renderSelect: (text: unknown, record: ProgramQuote & { key?: string }, index: number) => ReactNode;
    pinnedKey: string;
    expandedKey: string;
    renderExpandedRow: () => ReactNode;
    liveAmounts?: ProgramsTableLiveAmounts;
    testId: string;
    sortPreference?: ProgramsSortSelection | null;
    onSortChange?: (sort: ProgramsSortSelection | null) => void;
};

export const ProgramsTable: FC<ProgramsTableProps> = ({
    data,
    offerType,
    renderSelect,
    pinnedKey,
    expandedKey,
    renderExpandedRow,
    liveAmounts,
    testId,
    sortPreference,
    onSortChange
}) => {
    const isLenderProgramNamesRnREnabled = useSelector(featureToggleSelectors.isLenderProgramNamesRnREnabled);
    const isPIScreenEnhancementsEnabled = useSelector(featureToggleSelectors.isPIScreenEnhancementsEnabled);
    const isCashFixesEnabled = useSelector(featureToggleSelectors.isCashDealIncentivesFixesEnabled);
    const isStickyFiltersAndSortingEnabled = useSelector(featureToggleSelectors.isStickyFiltersAndSortingEnabled);
    const isWideEnoughForFullFrozenRegion = useMediaQuery({ query: `(min-width: ${PI_GRID_FROZEN_COLUMN_BREAKPOINT})` });
    const shouldFreezeColumns = isPIScreenEnhancementsEnabled && offerType !== CASH;
    const isCompactFrozenLayout = shouldFreezeColumns && !isWideEnoughForFullFrozenRegion;
    const frozenColumnCount = isCompactFrozenLayout ? PI_GRID_COMPACT_FROZEN_COLUMN_COUNT : PI_GRID_FROZEN_COLUMN_COUNT;

    const liveAppliedKey = liveAmounts?.appliedKey ?? '';
    const livePayment = liveAmounts?.payment;
    const liveBaselinePayment = liveAmounts?.baselinePayment;
    const liveRebates = liveAmounts?.rebates;
    const liveBaselineRebates = liveAmounts?.baselineRebates;
    const hasLiveAmounts = liveAmounts !== undefined;

    const columns = useMemo(() => {
        const renderColumns = createColumnsWithSorter(
            renderSelect,
            pinnedKey,
            isLenderProgramNamesRnREnabled,
            isPIScreenEnhancementsEnabled,
            isCompactFrozenLayout,
            hasLiveAmounts ? { appliedKey: liveAppliedKey, amount: livePayment, baseline: liveBaselinePayment } : undefined
        );
        const financeOnlyColumns = getFinanceOnlyColumns(isLenderProgramNamesRnREnabled);
        const cashExemptColumns = getCashExemptColumns(isLenderProgramNamesRnREnabled, isCashFixesEnabled);
        const financeExemptColumns = [leaseOnlyColumns, ...financeOnlyColumns.slice(0, -1)];
        const hiddenColumns = getGrossColumns(isPIScreenEnhancementsEnabled);

        const resolvedFinanceColumnsOrder = isPIScreenEnhancementsEnabled ? enhancedFinanceColumnsOrder : financeColumnsOrder;
        const resolvedBalloonColumnOrder = isPIScreenEnhancementsEnabled ? enhancedBalloonColumnOrder : balloonColumnOrder;
        const resolvedLeaseColumnsOrder = isPIScreenEnhancementsEnabled ? enhancedLeaseColumnsOrder : leaseColumnsOrder;

        const withLivePayment = (cols: TableColumn[]): TableColumn[] =>
            hasLiveAmounts
                ? withLiveAmountColumn(
                      withLivePaymentColumn(cols, liveAppliedKey, livePayment, liveBaselinePayment),
                      'appliedIncentiveAmount',
                      liveAppliedKey,
                      liveRebates,
                      liveBaselineRebates,
                      'Rebates',
                      true
                  )
                : cols;

        const withFreeze = (cols: TableColumn[]): TableColumn[] =>
            shouldFreezeColumns ? withFrozenColumns(cols, frozenColumnCount) : cols;

        const rebatesAfterRates = (order: (string | string[])[]): (string | string[])[] =>
            isStickyFiltersAndSortingEnabled && offerType !== CASH ? placeRebatesAfterRates(order) : order;

        const orderFor = (order: (string | string[])[]): (string | string[])[] =>
            isCompactFrozenLayout ? toCompactColumnsOrder(rebatesAfterRates(order)) : rebatesAfterRates(order);

        const withLayout = (cols: TableColumn[]): TableColumn[] => withFreeze(withLivePayment(cols));

        if (offerType === CASH) {
            return withLivePayment(removeColumns(renderColumns, cashExemptColumns));
        }
        if (offerType === FINANCE) {
            return withLayout(
                reorderColumnsByDataIndex(
                    removeColumns(renderColumns, [...financeExemptColumns, ...hiddenColumns]),
                    orderFor(resolvedFinanceColumnsOrder)
                )
            );
        }
        if (offerType === BALLOON) {
            return withLayout(
                reorderColumnsByDataIndex(
                    removeColumns(renderColumns, [...financeExemptColumns, ...hiddenColumns]),
                    orderFor(resolvedBalloonColumnOrder)
                )
            );
        }
        return withLayout(
            reorderColumnsByDataIndex(
                removeColumns(renderColumns, [...financeOnlyColumns, ...hiddenColumns]),
                orderFor(resolvedLeaseColumnsOrder)
            )
        );
    }, [
        renderSelect,
        pinnedKey,
        shouldFreezeColumns,
        frozenColumnCount,
        isCompactFrozenLayout,
        offerType,
        isLenderProgramNamesRnREnabled,
        isCashFixesEnabled,
        isPIScreenEnhancementsEnabled,
        isStickyFiltersAndSortingEnabled,
        hasLiveAmounts,
        liveAppliedKey,
        livePayment,
        liveBaselinePayment,
        liveRebates,
        liveBaselineRebates
    ]);

    const sortedColumns = useMemo(() => withRestoredSortOrder(columns, sortPreference), [columns, sortPreference]);

    const handleChangeSorter = useMemo<OnChangeSorterType | undefined>(
        () =>
            onSortChange &&
            ((sorter) => {
                const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;
                onSortChange(order ? { column: String(field), order } : null);
            }),
        [onSortChange]
    );

    const getRowClassName = useCallback(
        (record: ProgramQuote & { key?: string }) => getRowClassNameUtil(record, expandedKey),
        [expandedKey]
    );

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [frozenColumnsWidth, setFrozenColumnsWidth] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!shouldFreezeColumns) {
            setFrozenColumnsWidth(undefined);
            return undefined;
        }
        let frame = 0;
        const remeasure = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => setFrozenColumnsWidth(measureFrozenRegionOffset(tableWrapperRef.current)));
        };
        const observer = new ResizeObserver(remeasure);
        const wrapper = tableWrapperRef.current;
        if (wrapper) {
            observer.observe(wrapper, { box: 'border-box' });
        }
        findFrozenRegionCells(wrapper).forEach((cell) => observer.observe(cell, { box: 'border-box' }));
        document.addEventListener('scroll', remeasure, { capture: true, passive: true });
        remeasure();
        return () => {
            window.cancelAnimationFrame(frame);
            observer.disconnect();
            document.removeEventListener('scroll', remeasure, { capture: true });
        };
    }, [shouldFreezeColumns, frozenColumnCount, expandedKey]);

    return (
        <TableWrapperForDealCentral
            ref={tableWrapperRef}
            $appliedProgramKey={expandedKey}
            $frozenColumnsWidth={frozenColumnsWidth}
            $frozenColumnsEnabled={shouldFreezeColumns}
        >
            <Table
                data-testid={testId}
                columns={sortedColumns}
                rowClassName={getRowClassName}
                data={data}
                dataDensity="small"
                scrollX={'80rem'}
                sortableDetails={SORTABLE_DETAILS}
                onChangeSorter={handleChangeSorter}
                expandedRowComponent={renderExpandedRow}
                expandRowOnClick={true}
                highlightOnHover={false}
                expandedRows={[expandedKey]}
            />
        </TableWrapperForDealCentral>
    );
};
